// Training classes of a company, shown on the company profile page (see
// `features/training-classes.md`). Each class carries a free-text schedule and
// description, has coaches attached and athletes enrolled, and meets on dated
// sessions whose attendance a coach marks (the Attendance dialog). Reading
// comes with company access; creating, editing and deleting classes, coaches,
// enrolments, sessions and attendance are gated by MANAGE_TRAINING_CLASSES (the
// server enforces the same rules on every request). Every list/link mutation
// responds with the company's refreshed { classes, coaches, enrollments }
// snapshot, rendered directly.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Alert from '@mui/material/Alert';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import GroupsIcon from '@mui/icons-material/Groups';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SportsIcon from '@mui/icons-material/Sports';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import type {
  Athlete,
  TrainingClass,
  TrainingClassCoach,
  TrainingClassEnrollment,
  Weekday,
} from '../domain';
import { groupSchedulePatterns, schedulePatternLabel } from '../domain';
import type { TrainingClassesSnapshot } from '../data/apiClient';
import {
  deleteTrainingClass,
  fetchAthletes,
  fetchTrainingClasses,
  removeTrainingClassCoach,
  removeTrainingClassEnrollment,
} from '../data/apiClient';
import { useAuth } from '../state/AuthContext';
import { useTranslation } from '../state/I18nContext';
import { TrainingClassDialog } from './TrainingClassDialog';
import {
  TrainingClassMemberDialog,
  type TrainingMemberMode,
} from './TrainingClassMemberDialog';
import { TrainingAttendanceDialog } from './TrainingAttendanceDialog';

interface TrainingClassesSectionProps {
  companyId: number;
}

export function TrainingClassesSection({ companyId }: TrainingClassesSectionProps) {
  const { hasPermission } = useAuth();
  const { t } = useTranslation();
  const canManage = hasPermission('MANAGE_TRAINING_CLASSES');
  const dayLabel = (day: Weekday) => t(`admin.trainingClasses.weekdays.${day}`);

  const [classes, setClasses] = useState<TrainingClass[]>([]);
  const [coaches, setCoaches] = useState<TrainingClassCoach[]>([]);
  const [enrollments, setEnrollments] = useState<TrainingClassEnrollment[]>([]);
  const [athletes, setAthletes] = useState<Athlete[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [classDialogOpen, setClassDialogOpen] = useState(false);
  const [editingClass, setEditingClass] = useState<TrainingClass | null>(null);
  const [pendingClassDelete, setPendingClassDelete] = useState<TrainingClass | null>(null);
  const [memberDialog, setMemberDialog] = useState<{
    mode: TrainingMemberMode;
    trainingClass: TrainingClass;
  } | null>(null);
  const [attendanceClass, setAttendanceClass] = useState<TrainingClass | null>(null);

  const tRef = useRef(t);
  useEffect(() => {
    tRef.current = t;
  }, [t]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [snapshot, roster] = await Promise.all([
        fetchTrainingClasses(companyId),
        fetchAthletes(companyId),
      ]);
      setClasses(snapshot.classes);
      setCoaches(snapshot.coaches);
      setEnrollments(snapshot.enrollments);
      setAthletes(roster);
      setError(null);
    } catch (e: unknown) {
      setError(
        e instanceof Error ? e.message : tRef.current('admin.trainingClasses.errors.load'),
      );
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    void load();
  }, [load]);

  const applySnapshot = (snapshot: TrainingClassesSnapshot) => {
    setClasses(snapshot.classes);
    setCoaches(snapshot.coaches);
    setEnrollments(snapshot.enrollments);
  };

  // Refresh just the company roster — used after a coach/athlete is added so a
  // person created elsewhere resolves to their name here without a page reload.
  const reloadRoster = useCallback(async () => {
    try {
      setAthletes(await fetchAthletes(companyId));
    } catch {
      // Non-fatal: names fall back to the personal number until the next load.
    }
  }, [companyId]);

  const onMemberSaved = (snapshot: TrainingClassesSnapshot) => {
    applySnapshot(snapshot);
    void reloadRoster();
  };

  const athleteByNumber = useMemo(
    () => new Map(athletes.map((a) => [a.personalNumber, a])),
    [athletes],
  );
  const coachesByClass = useMemo(() => {
    const map = new Map<number, TrainingClassCoach[]>();
    for (const coach of coaches) {
      const list = map.get(coach.trainingClassId) ?? [];
      list.push(coach);
      map.set(coach.trainingClassId, list);
    }
    return map;
  }, [coaches]);
  const enrollmentsByClass = useMemo(() => {
    const map = new Map<number, TrainingClassEnrollment[]>();
    for (const enrollment of enrollments) {
      const list = map.get(enrollment.trainingClassId) ?? [];
      list.push(enrollment);
      map.set(enrollment.trainingClassId, list);
    }
    return map;
  }, [enrollments]);

  const athleteName = (athleteId: string) =>
    athleteByNumber.get(athleteId)?.fullName ?? athleteId;

  const runAction = async (
    action: () => Promise<TrainingClassesSnapshot>,
    fallbackKey: string,
  ) => {
    setBusy(true);
    setError(null);
    try {
      applySnapshot(await action());
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t(fallbackKey));
    } finally {
      setBusy(false);
    }
  };

  const confirmClassDelete = async () => {
    if (!pendingClassDelete) return;
    const target = pendingClassDelete;
    setPendingClassDelete(null);
    await runAction(
      () => deleteTrainingClass(target.id),
      'admin.trainingClasses.errors.delete',
    );
  };

  const attendanceEnrollments = attendanceClass
    ? (enrollmentsByClass.get(attendanceClass.id) ?? [])
    : [];

  return (
    <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
      <Stack direction="row" alignItems="center">
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          {t('admin.trainingClasses.title')}
        </Typography>
        {canManage && (
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon />}
            disabled={busy || loading}
            onClick={() => {
              setEditingClass(null);
              setClassDialogOpen(true);
            }}
          >
            {t('admin.trainingClasses.addClass')}
          </Button>
        )}
      </Stack>
      {/* One sentence stating what a training group IS — a recurring group on a
          fixed weekly schedule — since the word alone hasn't carried it. */}
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
        {t('admin.trainingClasses.subtitle')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : classes.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t(canManage ? 'admin.trainingClasses.emptyEditable' : 'admin.trainingClasses.empty')}
        </Typography>
      ) : (
        <Stack spacing={2}>
          {classes.map((trainingClass) => {
            const classCoaches = coachesByClass.get(trainingClass.id) ?? [];
            const classEnrollments = enrollmentsByClass.get(trainingClass.id) ?? [];
            return (
              <Paper key={trainingClass.id} variant="outlined" sx={{ p: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
                  <GroupsIcon color="primary" />
                  <Typography
                    variant="subtitle1"
                    noWrap
                    title={trainingClass.name}
                    sx={{ minWidth: 0 }}
                  >
                    {trainingClass.name}
                  </Typography>
                  {trainingClass.audience !== 'ALL' && (
                    <Chip
                      size="small"
                      variant="outlined"
                      color={trainingClass.audience === 'KIDS' ? 'warning' : 'info'}
                      label={t(`admin.trainingClasses.audience.${trainingClass.audience}`)}
                      sx={{ flexShrink: 0 }}
                    />
                  )}
                  <Box sx={{ flexGrow: 1 }} />
                  <Tooltip title={t('admin.trainingClasses.attendanceAction')}>
                    <span>
                      <IconButton
                        aria-label={t('admin.trainingClasses.attendanceAria', {
                          name: trainingClass.name,
                        })}
                        disabled={busy}
                        onClick={() => setAttendanceClass(trainingClass)}
                      >
                        <FactCheckIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  {canManage && (
                    <>
                      <Tooltip title={t('admin.trainingClasses.addCoach')}>
                        <span>
                          <IconButton
                            aria-label={t('admin.trainingClasses.addCoachAria', {
                              name: trainingClass.name,
                            })}
                            disabled={busy}
                            onClick={() =>
                              setMemberDialog({ mode: 'coach', trainingClass })
                            }
                          >
                            <SportsIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={t('admin.trainingClasses.addAthlete')}>
                        <span>
                          <IconButton
                            aria-label={t('admin.trainingClasses.addAthleteAria', {
                              name: trainingClass.name,
                            })}
                            disabled={busy}
                            onClick={() =>
                              setMemberDialog({ mode: 'athlete', trainingClass })
                            }
                          >
                            <PersonAddIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={t('admin.trainingClasses.editAria', { name: trainingClass.name })}>
                        <span>
                          <IconButton
                            aria-label={t('admin.trainingClasses.editAria', {
                              name: trainingClass.name,
                            })}
                            disabled={busy}
                            onClick={() => {
                              setEditingClass(trainingClass);
                              setClassDialogOpen(true);
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={t('admin.trainingClasses.deleteAria', { name: trainingClass.name })}>
                        <span>
                          <IconButton
                            aria-label={t('admin.trainingClasses.deleteAria', {
                              name: trainingClass.name,
                            })}
                            disabled={busy}
                            onClick={() => setPendingClassDelete(trainingClass)}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </>
                  )}
                </Stack>

                {/* The group's weekly rhythm, one line per meeting time
                    ("Meets Tue, Thu · 16:00–17:30"). An empty schedule is
                    called out rather than omitted — a group that never meets
                    should look incomplete. */}
                <Stack
                  direction="row"
                  alignItems="flex-start"
                  spacing={0.75}
                  sx={{ pl: 4.5, mb: 0.5 }}
                >
                  <ScheduleIcon
                    fontSize="small"
                    color="action"
                    sx={{ mt: '2px' }}
                  />
                  {trainingClass.schedule.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      {t('admin.trainingClasses.noSchedule')}
                    </Typography>
                  ) : (
                    <Stack spacing={0.25}>
                      {groupSchedulePatterns(trainingClass.schedule).map((pattern, i) => (
                        <Typography key={i} variant="body2" sx={{ fontWeight: 500 }}>
                          {t('admin.trainingClasses.meets', {
                            pattern: schedulePatternLabel(pattern, dayLabel),
                          })}
                        </Typography>
                      ))}
                    </Stack>
                  )}
                </Stack>
                {trainingClass.description && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ pl: 4.5, mb: 1, whiteSpace: 'pre-wrap' }}
                  >
                    {trainingClass.description}
                  </Typography>
                )}

                {/* Coaches */}
                <Box sx={{ pl: 4.5, mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    {t('admin.trainingClasses.coachesLabel')}
                  </Typography>
                  {classCoaches.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      {t('admin.trainingClasses.noCoaches')}
                    </Typography>
                  ) : (
                    <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 0.5 }}>
                      {classCoaches.map((coach) => (
                        <Chip
                          key={coach.id}
                          size="small"
                          color="secondary"
                          icon={<SportsIcon />}
                          label={athleteName(coach.athleteId)}
                          onDelete={
                            canManage
                              ? () =>
                                  void runAction(
                                    () => removeTrainingClassCoach(coach.id),
                                    'admin.trainingClasses.errors.member',
                                  )
                              : undefined
                          }
                        />
                      ))}
                    </Stack>
                  )}
                </Box>

                {/* Enrolled athletes */}
                <Box sx={{ pl: 4.5, mt: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    {t('admin.trainingClasses.athletesLabel', {
                      count: classEnrollments.length,
                    })}
                  </Typography>
                  {classEnrollments.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      {t('admin.trainingClasses.noAthletes')}
                    </Typography>
                  ) : (
                    <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                      {classEnrollments.map((enrollment) => {
                        const name = athleteName(enrollment.athleteId);
                        return (
                          <Stack
                            key={enrollment.id}
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            sx={{ py: 0.25 }}
                          >
                            <Typography
                              variant="body2"
                              noWrap
                              title={name}
                              sx={{ minWidth: 0 }}
                            >
                              {name}
                            </Typography>
                            <Chip
                              size="small"
                              variant="outlined"
                              label={enrollment.athleteId}
                              sx={{ fontFamily: 'monospace' }}
                            />
                            <Box sx={{ flexGrow: 1 }} />
                            {canManage && (
                              <Tooltip
                                title={t('admin.trainingClasses.removeAthleteAria', { name })}
                              >
                                <span>
                                  <IconButton
                                    size="small"
                                    aria-label={t('admin.trainingClasses.removeAthleteAria', {
                                      name,
                                    })}
                                    disabled={busy}
                                    onClick={() =>
                                      void runAction(
                                        () => removeTrainingClassEnrollment(enrollment.id),
                                        'admin.trainingClasses.errors.member',
                                      )
                                    }
                                  >
                                    <PersonRemoveIcon fontSize="inherit" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                          </Stack>
                        );
                      })}
                    </Stack>
                  )}
                </Box>
              </Paper>
            );
          })}
        </Stack>
      )}

      <TrainingClassDialog
        open={classDialogOpen}
        companyId={companyId}
        trainingClass={editingClass}
        onClose={() => setClassDialogOpen(false)}
        onSaved={applySnapshot}
      />

      {memberDialog && (
        <TrainingClassMemberDialog
          open={memberDialog !== null}
          mode={memberDialog.mode}
          trainingClass={memberDialog.trainingClass}
          takenIds={
            new Set(
              (memberDialog.mode === 'coach'
                ? (coachesByClass.get(memberDialog.trainingClass.id) ?? [])
                : (enrollmentsByClass.get(memberDialog.trainingClass.id) ?? [])
              ).map((m) => m.athleteId),
            )
          }
          onClose={() => setMemberDialog(null)}
          onSaved={onMemberSaved}
        />
      )}

      {attendanceClass && (
        <TrainingAttendanceDialog
          open={attendanceClass !== null}
          trainingClass={attendanceClass}
          enrollments={attendanceEnrollments}
          athletes={athletes}
          canManage={canManage}
          onClose={() => setAttendanceClass(null)}
        />
      )}

      <Dialog
        open={pendingClassDelete !== null}
        onClose={() => setPendingClassDelete(null)}
      >
        <DialogTitle>{t('admin.trainingClasses.deleteDialog.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {pendingClassDelete
              ? t('admin.trainingClasses.deleteDialog.body', {
                  name: pendingClassDelete.name,
                })
              : ''}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingClassDelete(null)}>
            {t('admin.common.cancel')}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => void confirmClassDelete()}
          >
            {t('admin.common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
